/**
 * Trailblazers (TRL) radar-range types.
 *
 * A dig is scored against an admin-authored list of "radar ranges" rather than
 * two fixed thresholds. The FIRST range is always the winning/capture radius;
 * every later range is a custom "near" band that drives the Status/Notification
 * overlays. Ranges live game-level on `games.trl_radar_config` (inherited by
 * every node) and, when a node overrides, in full on that node's
 * `trl_map_nodes.radar_config` (all-or-nothing, no merge) — mirroring
 * the stalemate-config pattern (see {@link resolveTrlRadarConfig}).
 *
 * Distinct from the empowered-scanner stalemate clue: that is a one-off,
 * unbounded distance reveal for struggling teams; radar Proximity is a
 * repeatable, ≤120 m reveal baked into a range.
 */
// ============================================================================
// Bounds
// ============================================================================
/** A radar config carries 1..4 ranges. */
export const TRL_RADAR_MIN_RANGES = 1;
export const TRL_RADAR_MAX_RANGES = 4;
/** Each range distance is an integer in [1, 120] metres. */
export const TRL_RADAR_MIN_DISTANCE = 1;
export const TRL_RADAR_MAX_DISTANCE = 120;
/** Obvious presets offered in the admin dropdown, in menu order. */
export const TRL_RADAR_COLOR_PRESETS = [
    { label: 'Red', value: '#e74c3c' },
    { label: 'Orange', value: '#f4a259' },
    { label: 'Yellow', value: '#f1c40f' },
    { label: 'Green', value: '#2ecc71' },
    { label: 'Blue', value: '#3b82f6' },
    { label: 'White', value: '#ffffff' },
];
/** Sensible defaults applied to every node until the admin changes them. */
export const DEFAULT_TRL_RADAR_CONFIG = {
    ranges: [
        { distanceMeters: 5, color: '#2ecc71', message: 'Found', showProximity: false },
        { distanceMeters: 30, color: '#f4a259', message: 'Close', showProximity: true },
        { distanceMeters: 120, color: '#f1c40f', message: 'Something Near', showProximity: false },
    ],
};
/**
 * Resolve the effective config for a node: a per-node override wins wholesale;
 * otherwise the game-level defaults; otherwise the built-in defaults. All-or-
 * nothing pick (no deep merge), matching {@link resolveTrlStalemateConfig}.
 */
export function resolveTrlRadarConfig(gameConfig, nodeOverride) {
    return nodeOverride ?? gameConfig ?? DEFAULT_TRL_RADAR_CONFIG;
}
/**
 * Score a distance against a radar config. Returns the first (smallest) range
 * the distance falls within — `index 0` = capture/win, `index > 0` = a near
 * band — or `null` when the distance exceeds the last range (a miss). Sorts a
 * copy so callers need not pre-sort.
 */
export function classifyTrlRadarDistance(config, distance) {
    const ranges = [...config.ranges].sort((a, b) => a.distanceMeters - b.distanceMeters);
    for (let i = 0; i < ranges.length; i++) {
        if (distance <= ranges[i].distanceMeters)
            return { index: i, range: ranges[i] };
    }
    return null;
}
/**
 * Value-equality across every persisted field. Used by the per-node editor to
 * decide whether a working copy still matches the game defaults — if so it is
 * stored as `null` (inherit) rather than a frozen override, so future changes to
 * the defaults keep propagating.
 */
export function trlRadarConfigEquals(a, b) {
    if (a.ranges.length !== b.ranges.length)
        return false;
    return a.ranges.every((r, i) => {
        const o = b.ranges[i];
        return (r.distanceMeters === o.distanceMeters &&
            r.color === o.color &&
            r.message === o.message &&
            r.showProximity === o.showProximity);
    });
}
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
/** Fallback colour when an authored value is missing/invalid. */
const FALLBACK_RADAR_COLOR = '#f1c40f';
function normalizeRadarColor(raw) {
    if (typeof raw === 'string' && HEX_COLOR_RE.test(raw.trim()))
        return raw.trim().toLowerCase();
    return FALLBACK_RADAR_COLOR;
}
/**
 * Sanitise untrusted admin input into a valid {@link TrlRadarConfig}: clamp to
 * 1..4 ranges, coerce each distance to an integer in [1, 120], require a
 * non-empty message and a valid hex colour, and force STRICTLY-ASCENDING
 * distances (dropping any trailing band that can no longer fit under 120 m).
 * Falls back to {@link DEFAULT_TRL_RADAR_CONFIG} when nothing usable remains.
 * Mirrors `normalizeStalemateConfig`.
 */
export function normalizeTrlRadarConfig(raw) {
    const fallback = () => ({
        ranges: DEFAULT_TRL_RADAR_CONFIG.ranges.map((r) => ({ ...r })),
    });
    if (!raw || typeof raw !== 'object')
        return fallback();
    const rawRanges = raw.ranges;
    if (!Array.isArray(rawRanges) || rawRanges.length === 0)
        return fallback();
    const cleaned = [];
    for (const item of rawRanges.slice(0, TRL_RADAR_MAX_RANGES)) {
        if (!item || typeof item !== 'object')
            continue;
        const it = item;
        const dRaw = Number(it.distanceMeters);
        if (!Number.isFinite(dRaw))
            continue;
        const distanceMeters = Math.min(TRL_RADAR_MAX_DISTANCE, Math.max(TRL_RADAR_MIN_DISTANCE, Math.round(dRaw)));
        const message = typeof it.message === 'string' && it.message.trim() ? it.message.trim() : 'Near';
        cleaned.push({
            distanceMeters,
            color: normalizeRadarColor(it.color),
            message,
            showProximity: Boolean(it.showProximity),
        });
    }
    if (cleaned.length === 0)
        return fallback();
    // Sort ascending then force strictly-increasing distances (bump duplicates;
    // drop any band that can no longer stay under the 120 m ceiling).
    cleaned.sort((a, b) => a.distanceMeters - b.distanceMeters);
    const ranges = [];
    let prev = 0;
    for (const r of cleaned) {
        const distanceMeters = Math.max(r.distanceMeters, prev + 1);
        if (distanceMeters > TRL_RADAR_MAX_DISTANCE)
            break;
        ranges.push({ ...r, distanceMeters });
        prev = distanceMeters;
    }
    if (ranges.length === 0)
        return fallback();
    return { ranges };
}
